import React, { useState, useCallback, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, CheckCircle, Loader2, User, Mail, Phone, MessageSquare } from 'lucide-react';
import { useContactForm } from '../../hooks/useContactForm';
import { toast } from 'react-toastify';

// Memoized version of the input field to prevent unnecessary re-renders
const InputField = memo(({
  icon: Icon,
  label,
  name,
  type = "text",
  value,
  required = false,
  placeholder,
  onChange,
  subjects
}: {
  icon: React.ElementType;
  label: string;
  name: string;
  type?: string;
  value: string;
  required?: boolean;
  placeholder?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  subjects?: Array<{ value: string; label: string; }>;
}) => {
  const fieldId = `contact-${name}`;
  
  return (
    <div className="relative">
      <label htmlFor={fieldId} className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        <div className="absolute left-3 top-3 text-gray-400">
          <Icon size={18} />
        </div>
        {type === "textarea" ? (
          <textarea
            id={fieldId}
            name={name}
            value={value}
            onChange={onChange}
            required={required}
            rows={5}
            className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors resize-none"
            placeholder={placeholder}
            aria-label={label}
          ></textarea>
        ) : type === "select" ? (
          <select
            id={fieldId}
            name={name}
            value={value}
            onChange={onChange}
            required={required}
            className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors appearance-none bg-white"
            aria-label={label}
          >
            {subjects?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            id={fieldId}
            type={type}
            name={name}
            value={value}
            onChange={onChange}
            required={required}
            placeholder={placeholder}
            className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
            aria-label={label}
          />
        )}
      </div>
    </div>
  );
});

const ContactForm = () => {
  const { loading, submitContactForm } = useContactForm();
  
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  
  const subjects = [
    { value: '', label: 'Seleziona argomento' },
    { value: 'info-vendita', label: 'Informazioni su vendita immobile' },
    { value: 'info-acquisto', label: 'Informazioni su acquisto immobile' },
    { value: 'info-affitto', label: 'Informazioni su affitto' },
    { value: 'valutazione', label: 'Richiesta valutazione gratuita' },
    { value: 'altro', label: 'Altro' },
  ];
  
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormState(prev => ({ ...prev, [name]: value }));
  }, []);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('submitting');
    
    try {
      // Invia i dati del form tramite hook useContactForm
      const { success, error } = await submitContactForm({
        name: formState.name,
        email: formState.email,
        phone: formState.phone,
        message: `Oggetto: ${formState.subject}\n${formState.message}`,
        type: 'generic'
      });
      
      if (success) {
        setFormStatus('success');
        setFormState({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
        });
        toast.success('Messaggio inviato con successo!');
      } else {
        setFormStatus('error');
        toast.error(`Errore nell'invio del messaggio: ${error}`);
      }
    } catch (error) {
      setFormStatus('error');
      toast.error("Si è verificato un errore nell'invio del messaggio. Riprova più tardi.");
    }
  };
  
  return (
    <AnimatePresence mode="wait">
      {formStatus === 'success' ? (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="bg-white rounded-xl shadow-lg p-8 relative overflow-hidden"
        >
          <div className="absolute top-0 left-0 right-0 h-2 bg-green-500"></div>
          <div className="text-center py-6">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 text-green-600 mb-6"
            >
              <CheckCircle className="h-10 w-10" />
            </motion.div>
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <h3 className="text-2xl font-semibold text-gray-800 mb-4">Messaggio Inviato!</h3>
              <p className="text-gray-600 mb-8">
                Grazie per averci contattato. Un membro del nostro team ti risponderà al più presto.
              </p>
              <button
                onClick={() => setFormStatus('idle')}
                className="inline-block px-6 py-3 bg-primary hover:bg-primary-light text-white font-semibold rounded-lg transition-colors shadow-md"
              >
                Invia un altro messaggio
              </button>
            </motion.div>
          </div>
        </motion.div>
      ) : (
        <motion.form 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onSubmit={handleSubmit} 
          className="bg-white rounded-xl shadow-lg p-8 relative overflow-hidden"
        >
          <div className="absolute top-0 left-0 right-0 h-2 bg-primary"></div>
          <h3 className="text-2xl font-semibold text-gray-800 mb-6 text-center">Inviaci un Messaggio</h3>
          
          <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <motion.div
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.1 }}
              >
                <InputField 
                  icon={User} 
                  label="Nome e Cognome" 
                  name="name" 
                  value={formState.name}
                  onChange={handleChange}
                  required
                  placeholder="Il tuo nome completo"
                  subjects={subjects}
                />
              </motion.div>
              
              <motion.div
                initial={{ x: 20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <InputField 
                  icon={Phone} 
                  label="Telefono" 
                  name="phone" 
                  type="tel"
                  value={formState.phone}
                  onChange={handleChange}
                  required
                  placeholder="Il tuo numero di telefono"
                  subjects={subjects}
                />
              </motion.div>
            </div>
            
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <InputField 
                icon={Mail} 
                label="Email" 
                name="email" 
                type="email"
                value={formState.email}
                onChange={handleChange}
                required
                placeholder="La tua email"
                subjects={subjects}
              />
            </motion.div>
            
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              <InputField 
                icon={MessageSquare} 
                label="Argomento" 
                name="subject" 
                type="select"
                value={formState.subject}
                onChange={handleChange}
                required
                placeholder="Seleziona argomento"
                subjects={subjects}
              />
            </motion.div>
            
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <InputField 
                icon={MessageSquare} 
                label="Messaggio" 
                name="message" 
                type="textarea"
                value={formState.message}
                onChange={handleChange}
                required
                placeholder="Scrivi qui il tuo messaggio..."
                subjects={subjects}
              />
            </motion.div>
            
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="flex items-start mt-4"
            >
              <input
                type="checkbox"
                id="privacy"
                required
                className="h-5 w-5 text-primary border-gray-300 rounded mt-0.5 focus:ring-primary"
              />
              <label htmlFor="privacy" className="ml-2 block text-sm text-gray-600">
                Autorizzo il trattamento dei dati personali ai sensi del D.Lgs. 196/2003 e del GDPR (Regolamento UE 2016/679) <span className="text-red-500">*</span>
              </label>
            </motion.div>
            
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="flex justify-end"
            >
              <button
                type="submit"
                disabled={loading || formStatus === 'submitting'}
                className="inline-flex items-center justify-center bg-primary hover:bg-primary-light text-white font-semibold px-8 py-3 rounded-lg transition-colors shadow-md disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading || formStatus === 'submitting' ? (
                  <>
                    <Loader2 className="animate-spin h-5 w-5 mr-3" />
                    Invio in corso...
                  </>
                ) : (
                  <>
                    Invia Messaggio
                    <Send className="ml-2 h-5 w-5" />
                  </>
                )}
              </button>
            </motion.div>
          </div>
        </motion.form>
      )}
    </AnimatePresence>
  );
};

export default ContactForm;